import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import ConfirmationPopup from "../../../../packages/ConfirmationPopup";
import CustomModal from "../../../../packages/CustomModal";
import IconButton from "../../../../packages/IconButton";
import CloseIcon from "../../../../packages/SVGs/CloseIcon";
import WPDropdown from "../../../../packages/WPDropdown";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../../packages/WPMessageBanner/WPMessageBanner";

import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../../components/Notifications";

import {
  CustomModalButtons,
  FILE_SHEET_MAPPING_COLUMN_TYPES,
  LOCAL_STORAGE_KEYS,
  TROUBLESHOOTING_TOPICS,
} from "../../../../constants/global.constants";

import {
  ArrowLeftIcon,
  CopyIcon,
  QuestionIcon,
} from "../../../../assets/icons";

import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "../../../../utils/session";

import {
  getFileSheetMapping,
  updateFileSheetMapping,
} from "../../../../network/cipAPI";

import { storeFileSheetMapping } from "../../../../Store/TemplateStore/template.action";

const getTroubleshootingReadTopics = () =>
  JSON.parse(
    getLocalStorageItem(LOCAL_STORAGE_KEYS.TROUBLESHOOTING_READ_TOPICS) || "[]",
  );

const hasReadFileSheetMappingHelp = (troubleshootingReadTopics) =>
  troubleshootingReadTopics.includes(TROUBLESHOOTING_TOPICS.FILE_SHEET_MAPPING);

const getUploadedFileCardsFromResponse = (response) =>
  (response?.uploaded_files || []).map((uploadedFile, index) => ({
    id: `${uploadedFile?.plant_file_id || index}`,
    fileName: uploadedFile?.file_name,
    sheets: Array.isArray(uploadedFile?.sheets)
      ? uploadedFile.sheets.map((sheet) => ({
          sheetName: sheet?.sheet,
          trainNums: Array.isArray(sheet?.train_nums) ? sheet.train_nums : [],
        }))
      : [],
  }));

const getTrainNumbers = (trainMappings, uploadedFileCards) => {
  const mappingTrains = (trainMappings || [])
    .map((mapping) => Number(mapping?.train_num))
    .filter((trainNum) => Number.isInteger(trainNum) && trainNum > 0);

  if (mappingTrains.length) {
    return [...new Set(mappingTrains)].sort((first, second) => first - second);
  }

  const uploadedSheetTrains = (uploadedFileCards || []).flatMap((fileCard) =>
    (fileCard?.sheets || []).flatMap((sheet) =>
      (sheet?.trainNums || [])
        .map((trainNum) => Number(trainNum))
        .filter((trainNum) => Number.isInteger(trainNum) && trainNum > 0),
    ),
  );

  if (uploadedSheetTrains.length) {
    return [...new Set(uploadedSheetTrains)].sort(
      (first, second) => first - second,
    );
  }

  return [1];
};

const buildRows = ({
  trainNumbers,
  uploadedFileCards,
  trainMappings,
  allSheetOptions,
  t,
}) => {
  const mappingByTrain = new Map(
    (trainMappings || []).map((mapping) => [
      Number(mapping?.train_num),
      mapping,
    ]),
  );

  return trainNumbers.map((trainNum) => {
    const trainMapping = mappingByTrain.get(trainNum);
    const mappedFileFromId = (uploadedFileCards || []).find(
      (fileCard) =>
        Number(fileCard?.id) === Number(trainMapping?.plant_file_id),
    );
    const selectedFile =
      trainMapping?.file_name || mappedFileFromId?.fileName || "";
    const sheetsForSelectedFile =
      (uploadedFileCards || [])
        .find((fileCard) => fileCard?.fileName === selectedFile)
        ?.sheets?.map((sheet) => sheet?.sheetName)
        ?.filter(Boolean) || [];

    const selectedSheet = trainMapping?.sheet_name || "";

    const colType = trainMapping?.col_mapping_type || "";
    const selectedMode =
      colType === FILE_SHEET_MAPPING_COLUMN_TYPES.MANUAL ||
      colType === FILE_SHEET_MAPPING_COLUMN_TYPES.AUTO
        ? colType
        : "";

    return {
      trainNum,
      trainLabel: `${t("ValueStrings.train")} ${trainNum}`,
      file: selectedFile,
      sheet:
        selectedSheet &&
        (sheetsForSelectedFile.includes(selectedSheet) ||
          allSheetOptions.includes(selectedSheet))
          ? selectedSheet
          : "",
      column: selectedMode,
      hasChanges: false,
    };
  });
};

export const useFileSheetMappingFlow = ({ plantId, sheetNames }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showMainModal, setShowMainModal] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showResetWarning, setShowResetWarning] = useState(false);
  const [helpOpenedFromMainModal, setHelpOpenedFromMainModal] = useState(false);
  const [isFileSheetMappingLoading, setIsFileSheetMappingLoading] =
    useState(false);
  const [isFileSheetMappingSaving, setIsFileSheetMappingSaving] =
    useState(false);
  const [fileSheetMappingError, setFileSheetMappingError] = useState("");
  const [fileSheetMappingResponse, setFileSheetMappingResponse] = useState({
    uploaded_files: [],
    train_mappings: [],
  });

  const responseUploadedFileCards = useMemo(
    () => getUploadedFileCardsFromResponse(fileSheetMappingResponse),
    [fileSheetMappingResponse],
  );

  const normalizedTrainMappings = useMemo(
    () =>
      Array.isArray(fileSheetMappingResponse?.train_mappings)
        ? fileSheetMappingResponse.train_mappings
        : [],
    [fileSheetMappingResponse],
  );

  const fileOptions = useMemo(
    () =>
      (responseUploadedFileCards || [])
        .map((fileCard) => fileCard?.fileName)
        .filter(Boolean),
    [responseUploadedFileCards],
  );

  const sheetsByFile = useMemo(
    () =>
      (responseUploadedFileCards || []).reduce((accumulator, fileCard) => {
        const fileName = fileCard?.fileName;
        if (!fileName) return accumulator;

        accumulator[fileName] = (fileCard?.sheets || [])
          .map((sheet) => sheet?.sheetName)
          .filter(Boolean);

        return accumulator;
      }, {}),
    [responseUploadedFileCards],
  );

  const allSheetOptions = useMemo(() => {
    const fromFiles = Object.values(sheetsByFile).flat();
    const fallbackSheets = Array.isArray(sheetNames) ? sheetNames : [];
    return [...new Set([...fromFiles, ...fallbackSheets].filter(Boolean))];
  }, [sheetsByFile, sheetNames]);

  const trainNumbers = useMemo(
    () => getTrainNumbers(normalizedTrainMappings, responseUploadedFileCards),
    [normalizedTrainMappings, responseUploadedFileCards],
  );

  const fileDropdownOptions = useMemo(
    () => fileOptions.map((option) => ({ label: option, value: option })),
    [fileOptions],
  );

  const columnDefaultValue = t("fileSheetMapping.columnDefault");
  const columnManualValue = t("fileSheetMapping.columnManual");

  const columnDropdownOptions = useMemo(
    () => [
      {
        label: columnManualValue,
        value: FILE_SHEET_MAPPING_COLUMN_TYPES.MANUAL,
      },
      {
        label: columnDefaultValue,
        value: FILE_SHEET_MAPPING_COLUMN_TYPES.AUTO,
      },
    ],
    [columnDefaultValue, columnManualValue],
  );

  const initialRows = useMemo(
    () =>
      buildRows({
        trainNumbers,
        uploadedFileCards: responseUploadedFileCards,
        trainMappings: normalizedTrainMappings,
        allSheetOptions,
        t,
      }),
    [
      trainNumbers,
      responseUploadedFileCards,
      normalizedTrainMappings,
      allSheetOptions,
      t,
    ],
  );

  const [mappingRows, setMappingRows] = useState(() => initialRows);

  useEffect(() => {
    setMappingRows(initialRows);
  }, [initialRows]);

  const hasMappingChanges = useMemo(() => {
    if (mappingRows.length !== initialRows.length) {
      return true;
    }

    return mappingRows.some((row, index) => {
      const initialRow = initialRows[index];
      return (
        row?.file !== initialRow?.file ||
        row?.sheet !== initialRow?.sheet ||
        row?.column !== initialRow?.column
      );
    });
  }, [mappingRows, initialRows]);

  const hasIncompleteRows = useMemo(
    () =>
      mappingRows.some(
        (row) =>
          !(row?.file || "").trim() ||
          !(row?.sheet || "").trim() ||
          !(row?.column || "").trim(),
      ),
    [mappingRows],
  );

  const refreshRows = () => {
    setMappingRows(initialRows);
  };

  const buildMappingsPayload = () => {
    const mappings = mappingRows
      .filter((row) => row?.hasChanges)
      .map((row) => {
        const selectedFile = responseUploadedFileCards.find(
          (fileCard) => fileCard?.fileName === row.file,
        );
        const plantFileId = Number(selectedFile?.id);

        return {
          train_num: Number(row.trainNum),
          plant_file_id: Number.isNaN(plantFileId) ? null : plantFileId,
          sheet_name: row.sheet || "",
          col_mapping_type: (() => {
            const colType = row.column || "";

            if (
              colType === FILE_SHEET_MAPPING_COLUMN_TYPES.MANUAL ||
              colType === FILE_SHEET_MAPPING_COLUMN_TYPES.AUTO
            ) {
              return colType;
            }

            return "";
          })(),
        };
      });

    return { mappings };
  };

  const fetchFileSheetMappingData = async () => {
    if (!plantId) {
      setFileSheetMappingError(t("notificationStrings.somethingWrong"));
      return false;
    }

    setIsFileSheetMappingLoading(true);
    setFileSheetMappingError("");

    try {
      const response = await getFileSheetMapping(plantId);
      const responseData = response?.data || {};

      setFileSheetMappingResponse({
        uploaded_files: Array.isArray(responseData?.uploaded_files)
          ? responseData.uploaded_files
          : [],
        train_mappings: Array.isArray(responseData?.train_mappings)
          ? responseData.train_mappings
          : [],
      });

      return true;
    } catch (error) {
      setFileSheetMappingError(t("notificationStrings.somethingWrong"));
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
      return false;
    } finally {
      setIsFileSheetMappingLoading(false);
    }
  };

  const openFileSheetMappingFlow = async () => {
    const troubleshootingReadTopics = getTroubleshootingReadTopics();

    if (hasReadFileSheetMappingHelp(troubleshootingReadTopics)) {
      setShowMainModal(true);
      const isFetched = await fetchFileSheetMappingData();
      if (!isFetched) {
        return;
      }

      refreshRows();
      return;
    }

    const isFetched = await fetchFileSheetMappingData();
    if (!isFetched) {
      return;
    }

    refreshRows();

    setHelpOpenedFromMainModal(false);
    setShowHelpModal(true);
  };

  const markFileSheetMappingHelpAsRead = () => {
    const troubleshootingReadTopics = getTroubleshootingReadTopics();

    if (!hasReadFileSheetMappingHelp(troubleshootingReadTopics)) {
      const updatedTroubleshootingReadTopics = [
        ...troubleshootingReadTopics,
        TROUBLESHOOTING_TOPICS.FILE_SHEET_MAPPING,
      ];

      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.TROUBLESHOOTING_READ_TOPICS,
        JSON.stringify(updatedTroubleshootingReadTopics),
      );
    }
  };

  const handleHelpModalClose = ({ target }) => {
    const action = target?.id;

    if (action === CustomModalButtons.CONFIRM) {
      markFileSheetMappingHelpAsRead();
      setShowHelpModal(false);
      setHelpOpenedFromMainModal(false);
      setShowMainModal(true);
      return;
    }

    setShowHelpModal(false);

    if (helpOpenedFromMainModal) {
      setShowMainModal(true);
      setHelpOpenedFromMainModal(false);
    }
  };

  const handleHelpBackClick = () => {
    handleHelpModalClose({ target: { id: CustomModalButtons.CLOSE } });
  };

  const handleOpenHelpFromMain = () => {
    setHelpOpenedFromMainModal(true);
    setShowMainModal(false);
    setShowHelpModal(true);
  };

  const handleMainModalClose = ({ target }) => {
    const action = target?.id;

    if (action === CustomModalButtons.CONFIRM) {
      if (hasMappingChanges) {
        setShowResetWarning(true);
      } else {
        setShowMainModal(false);
      }
      return;
    }

    if (
      action === CustomModalButtons.CLOSE ||
      action === CustomModalButtons.CANCEL
    ) {
      if (hasMappingChanges) {
        setShowUnsavedWarning(true);
      } else {
        setShowMainModal(false);
      }
    }
  };

  const handleUnsavedWarningClose = (action) => {
    if (action === CustomModalButtons.CONFIRM) {
      setShowMainModal(false);
      setShowUnsavedWarning(false);
      return;
    }

    setShowUnsavedWarning(false);
  };

  const handleResetWarningClose = (action) => {
    if (action !== CustomModalButtons.CONFIRM) {
      setShowResetWarning(false);
      return;
    }

    // Close the confirmation popup immediately; continue save on the main modal.
    setShowResetWarning(false);

    const saveMappings = async () => {
      if (!plantId) {
        setFileSheetMappingError(t("notificationStrings.somethingWrong"));
        return;
      }

      setIsFileSheetMappingSaving(true);
      setFileSheetMappingError("");

      try {
        const payload = buildMappingsPayload();
        const response = await updateFileSheetMapping(plantId, payload);
        if (response?.status === 200 && response?.results?.length > 0) {
          dispatch(
            storeFileSheetMapping({
              plantId,
              mappings: response.results,
              updatedAt: Date.now(),
            }),
          );
        }

        setShowMainModal(false);
        setShowResetWarning(false);
      } catch (error) {
        setFileSheetMappingError(t("notificationStrings.somethingWrong"));
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setIsFileSheetMappingSaving(false);
      }
    };

    saveMappings();
  };

  const handleRowValueChange = (rowIndex, field) => (selectedOption) => {
    const value = selectedOption?.value || "";

    setMappingRows((previousRows) =>
      previousRows.map((row, index) =>
        index !== rowIndex
          ? row
          : (() => {
              const nextRow = { ...row, [field]: value, hasChanges: true };

              if (field === "file") {
                const sheetsForFile = value ? sheetsByFile[value] || [] : [];
                if (!sheetsForFile.includes(nextRow.sheet)) {
                  nextRow.sheet = "";
                }
              }

              return nextRow;
            })(),
      ),
    );
  };

  const handleCopyToBelowRows = (rowIndex) => () => {
    setMappingRows((previousRows) => {
      const sourceRow = previousRows[rowIndex];
      if (!sourceRow) return previousRows;

      return previousRows.map((row, index) => {
        if (index <= rowIndex) return row;

        const sheetsForFile = sourceRow.file
          ? sheetsByFile[sourceRow.file] || []
          : [];
        const nextSheet = sheetsForFile.includes(sourceRow.sheet)
          ? sourceRow.sheet
          : "";

        return {
          ...row,
          file: sourceRow.file,
          sheet: nextSheet,
          column: sourceRow.column,
          hasChanges: true,
        };
      });
    });
  };

  const renderDialogs = () => (
    <>
      {showHelpModal && (
        <CustomModal
          header={
            <div className="file-sheet-mapping-help__header">
              {helpOpenedFromMainModal && (
                <IconButton
                  className="file-sheet-mapping-help__back"
                  onClick={handleHelpBackClick}
                >
                  <ArrowLeftIcon />
                </IconButton>
              )}
              <span>{t("helpInformation.fileSheetMappingHelpHeader")}</span>
            </div>
          }
          onModalClose={handleHelpModalClose}
          confirmBtn={t("ValueStrings.okay")}
          customclass="file-sheet-mapping-help-modal"
          custombodyclass="file-sheet-mapping-help__body"
          width="560px"
          hideCloseIcon
        >
          <ol className="file-sheet-mapping-help__list">
            <li>{t("helpInformation.fileSheetMappingHelpPoint1")}</li>
            <li>
              {t("helpInformation.fileSheetMappingHelpPoint2")}
              <ul>
                <li>{t("helpInformation.fileSheetMappingHelpPoint2a")}</li>
                <li>{t("helpInformation.fileSheetMappingHelpPoint2b")}</li>
                <li>{t("helpInformation.fileSheetMappingHelpPoint2c")}</li>
              </ul>
            </li>
            <li>{t("helpInformation.fileSheetMappingHelpPoint3")}</li>
          </ol>
        </CustomModal>
      )}

      {showMainModal && (
        <CustomModal
          header={
            <div className="file-sheet-mapping-main__header">
              <span className="file-sheet-mapping-main__title">
                {t("fileSheetMapping.title")}
              </span>
              <div className="file-sheet-mapping-main__header-actions">
                <IconButton
                  className="file-sheet-mapping-main__troubleshoot-btn"
                  tooltip={t("fileSheetMapping.troubleshootTooltip")}
                  onClick={handleOpenHelpFromMain}
                >
                  <QuestionIcon />
                </IconButton>
                <IconButton
                  className="file-sheet-mapping-main__close-btn"
                  tooltip={t("ValueStrings.close")}
                  onClick={() =>
                    handleMainModalClose({
                      target: { id: CustomModalButtons.CLOSE },
                    })
                  }
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
          }
          onModalClose={handleMainModalClose}
          confirmBtn={t("fileSheetMapping.import")}
          cancelBtn={t("ValueStrings.cancel")}
          customclass="file-sheet-mapping-main-modal"
          custombodyclass="file-sheet-mapping-main__body"
          isLoading={isFileSheetMappingLoading || isFileSheetMappingSaving}
          confirmBtnDisabled={
            isFileSheetMappingLoading ||
            isFileSheetMappingSaving ||
            mappingRows.length === 0 ||
            hasIncompleteRows ||
            !hasMappingChanges
          }
          width="760px"
          hideCloseIcon
        >
          <WPMessageBanner
            type={BANNER_TYPES.info}
            message={t("fileSheetMapping.warning")}
            className="file-sheet-mapping-main__warning"
          />

          {!!fileSheetMappingError && (
            <WPMessageBanner
              type={BANNER_TYPES.error}
              message={fileSheetMappingError}
              className="file-sheet-mapping-main__warning"
            />
          )}

          <div className="file-sheet-mapping-main__grid">
            <div className="file-sheet-mapping-main__grid-header file-sheet-mapping-main__grid-header--train" />
            <div className="file-sheet-mapping-main__grid-header">
              {t("fileSheetMapping.files")}
            </div>
            <div className="file-sheet-mapping-main__grid-header">
              {t("fileSheetMapping.sheets")}
            </div>
            <div className="file-sheet-mapping-main__grid-header">
              {t("fileSheetMapping.columns")}
            </div>
            <div className="file-sheet-mapping-main__grid-header file-sheet-mapping-main__grid-header--copy" />

            {mappingRows.map((row, index) => (
              <div
                className="file-sheet-mapping-main__row"
                key={row.trainLabel}
              >
                {(() => {
                  const rowSheetOptions = row.file
                    ? (sheetsByFile[row.file] || []).map((option) => ({
                        label: option,
                        value: option,
                      }))
                    : [];
                  const isLastTrainRow = index === mappingRows.length - 1;
                  const isRowComplete = row.file && row.sheet && row.column;
                  const canCopyToBelow = !isLastTrainRow && isRowComplete;

                  return (
                    <>
                      <div className="file-sheet-mapping-main__train-label">
                        {row.trainLabel}
                      </div>

                      <WPDropdown
                        customClassName="file-sheet-mapping-main__select"
                        options={fileDropdownOptions}
                        value={row.file || null}
                        onChange={handleRowValueChange(index, "file")}
                      />

                      <WPDropdown
                        customClassName="file-sheet-mapping-main__select"
                        options={rowSheetOptions}
                        value={row.sheet || null}
                        disabled={!row.file}
                        onChange={handleRowValueChange(index, "sheet")}
                      />

                      <WPDropdown
                        customClassName="file-sheet-mapping-main__select"
                        options={columnDropdownOptions}
                        value={row.column || null}
                        disabled={!row.file}
                        onChange={handleRowValueChange(index, "column")}
                      />

                      {isLastTrainRow ? (
                        <div />
                      ) : (
                        <IconButton
                          className="file-sheet-mapping-main__copy-btn"
                          tooltip={t("fileSheetMapping.copyTooltip")}
                          isDisabledTooltip
                          disabled={!canCopyToBelow}
                          onClick={handleCopyToBelowRows(index)}
                        >
                          <CopyIcon />
                        </IconButton>
                      )}
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </CustomModal>
      )}

      {showUnsavedWarning && (
        <ConfirmationPopup
          type="danger"
          header={t("fileSheetMapping.unsavedHeader")}
          description={t("fileSheetMapping.unsavedDescription")}
          cancelBtn={t("fileSheetMapping.keepEditing")}
          confirmBtn={t("fileSheetMapping.discard")}
          onClose={handleUnsavedWarningClose}
          width="416px"
        />
      )}

      {showResetWarning && (
        <ConfirmationPopup
          type="danger"
          header={t("fileSheetMapping.resetHeader")}
          description={t("fileSheetMapping.resetDescription")}
          cancelBtn={t("ValueStrings.cancel")}
          confirmBtn={t("fileSheetMapping.resetConfirm")}
          onClose={handleResetWarningClose}
          width="416px"
        />
      )}
    </>
  );

  return {
    openFileSheetMappingFlow,
    renderFileSheetMappingDialogs: renderDialogs,
  };
};
